import { useCallback, useMemo, useState } from 'react';

import { breakpoints } from '@src/common/breakpoints';
import { useTranslate } from '@src/core/hooks';
import { resolveAdditionalChips, type DetailedSaleItem } from '@src/domains/sale-items';
import { formatScheduleDate, resolveScheduleDateDisplay, ScheduleDateDisplay } from '@src/domains/scheduling';
import type { SaleChange } from '@src/domains/sales';
import { useReservationVoucherDownload, useReservationVouchers } from '@src/features/vouchers';
import { useRescheduleNotice, useReservationRescheduling } from '@src/features/sales/reschedule';
import useMediaQuery from '@src/hooks/useMediaQuery';
import { formatReservationResources } from '@src/features/sale-items/utils/formatReservationResources';

export interface UseReservationCardParams {
  orderId: string;
  reservationId: string | null;
  saleItem: DetailedSaleItem;
  /** Order-level flag from useOrderDetails, which the reschedule hooks expect. */
  hasMultipleVouchers: boolean;
  isPackageItem?: boolean;
  onSaleChangeCheckout?: (saleChange: SaleChange) => void;
}

export function useReservationCard(params: UseReservationCardParams) {
  const { orderId, reservationId, saleItem, hasMultipleVouchers, isPackageItem = false, onSaleChangeCheckout } = params;

  const { i18n, locale } = useTranslate();
  const isCompact = useMediaQuery(breakpoints.smallTablet);

  const [showsAllAdditionals, setShowsAllAdditionals] = useState(false);
  const [isVoucherListOpen, setVoucherListOpen] = useState(false);

  const { vouchers, summary } = useReservationVouchers({
    orderId,
    saleItemId: saleItem.id,
    hasMultipleVouchers,
  });

  const notice = useRescheduleNotice(saleItem);

  const rescheduling = useReservationRescheduling({
    saleItem,
    orderId,
    isPackageItem,
    hasMultipleVouchers,
    hasVoucherSplitPolicy: summary.hasMultiple,
    onSaleChangeCheckout,
  });

  const { downloadAll, isDownloadingAll, hasDownloadableTargets } = useReservationVoucherDownload({
    reservationId,
    vouchers,
  });

  const schedule = useMemo(() => {
    const display = resolveScheduleDateDisplay({
      scheduleDate: saleItem.scheduleDate,
      scheduleTimeMinutes: saleItem.formattedScheduleTimeMinutes,
    });

    if (display === ScheduleDateDisplay.WithoutDate) {
      return { date: i18n('account_sale_item_without_date'), time: null };
    }

    return {
      date: formatScheduleDate(saleItem.scheduleDate ?? undefined, locale),
      time: display === ScheduleDateDisplay.DateAndTime ? saleItem.formattedScheduleTimeMinutes : null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n is stable per locale
  }, [saleItem.scheduleDate, saleItem.formattedScheduleTimeMinutes, locale]);

  const chips = useMemo(
    () => resolveAdditionalChips(saleItem.additionals, showsAllAdditionals ? saleItem.additionals.length : undefined),
    [saleItem.additionals, showsAllAdditionals],
  );

  const showAllAdditionals = useCallback(() => setShowsAllAdditionals(true), []);
  const openVoucherList = useCallback(() => setVoucherListOpen(true), []);
  const closeVoucherList = useCallback(() => setVoucherListOpen(false), []);

  return {
    productName: saleItem.product.name,
    supplier: { name: saleItem.product.app.name, logoUrl: saleItem.product.app.logoUrl },
    schedule,
    resources: formatReservationResources(saleItem.resources),
    additionals: {
      visible: chips.visible,
      overflowCount: chips.overflowCount,
      overflowAs: isCompact ? ('link' as const) : ('chip' as const),
      showAll: showAllAdditionals,
    },
    notice,
    reschedule: {
      showsTrigger: rescheduling.availability.isVisible,
      isEnabled: rescheduling.availability.isEnabled,
      tooltip: rescheduling.availability.showsPackageTooltip
        ? i18n('checkout_confirmed_reschedule_package_tooltip')
        : undefined,
      isOpen: rescheduling.state.isGoing,
      start: rescheduling.start,
      cancel: rescheduling.cancel,
      confirm: rescheduling.confirm,
    },
    vouchers,
    voucherSummary: summary,
    voucherList: { isOpen: isVoucherListOpen, open: openVoucherList, close: closeVoucherList },
    download: {
      run: downloadAll,
      isRunning: isDownloadingAll,
      isBlocked: summary.hasPending || summary.total === 0 || !hasDownloadableTargets,
      pendingTooltip: summary.hasPending ? i18n('checkout_confirmed_vouchers_pending_tooltip') : undefined,
    },
  };
}

export default useReservationCard;
